// ── Settings / Thresholds ("/settings/thresholds") — FR-SET (NOW LIVE) ──────
// Reads the NOW-LIVE GET /settings/thresholds (view.settings, ?orgId injected by
// the shared transport, bare { thresholds:[…] }) via useHonestQuery and renders
// the thresholds. SA/Admin get an inline per-threshold edit that POSTs the upsert
// with a generated UUID Idempotency-Key and toasts ONLY after the backend
// confirms; non-editors read-only. A threshold with no stored row shows a neutral
// "not set" placeholder — no value is fabricated. The 9 honest postures stay:
// loading is a skeleton, a denied/unavailable read renders its honest state,
// never a blank or an empty list dressed as data.
//
// ── PARTICIPANT EXCLUSION (release gate) ─────────────────────────────────────
// The admin Thresholds surface is STAFF/coach-where-allowed; participant is
// DENIED (route + page fail-closed). A participant's own settings are the
// separate, participant-reachable /settings/profile shell — NOT this admin page.

import { Alert, Badge, FormSurface, IconCog } from "@vjal/ui";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingReadinessServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingThresholdsResponseDto } from "../../services/coaching-readiness-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { ThresholdEditorRow } from "./threshold-editor-row";
import {
  canAccessThresholdSettings,
  canEditThresholds,
  mergeThresholdFields
} from "./thresholds-helpers";

const services = getCoachingReadinessServices();

export function ThresholdsPage({ role }: ProductScreenContext) {
  const moduleDenied = !canAccessThresholdSettings(role);
  const editable = canEditThresholds(role);

  // The read NEVER fires for a denied role (fail-closed): the query is disabled.
  const query = useHonestQuery<CoachingThresholdsResponseDto>(
    coachingQueryKeys.readiness.thresholds(),
    () => services.settings.thresholds(),
    {
      enabled: !moduleDenied,
      // The known knobs are always rendered (with "not set" when absent), so an
      // empty backend response is NOT an empty surface — treat it as ready.
      isEmpty: () => false
    }
  );

  if (moduleDenied) {
    return (
      <section aria-label="Thresholds" className="col-span-12" data-testid="thresholds-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            icon={<IconCog size={22} />}
            title="You don't have access to threshold settings"
            message="Coaching thresholds are managed by staff. Your own preferences live on your profile settings."
          />
        </div>
      </section>
    );
  }

  const fields = mergeThresholdFields(query.data?.thresholds);

  return (
    <section aria-label="Thresholds" className="col-span-12 flex flex-col gap-4" data-testid="thresholds-page">
      {/* F3: real @vjal/ui FormSurface for Form-1 settings-thresholds route. */}
      <FormSurface title="Thresholds & settings" hideSubmit className="col-span-12">
        <p className="m-0 text-sm text-ink-muted">Attendance, progress, and capstone thresholds for your organization.</p>
        <div data-testid="thresholds-shell" className="space-y-4">
          {query.posture !== null ? (
            <HonestState
              posture={query.posture}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconCog size={22} />}
              loadingVariant="rows"
            />
          ) : (
            <>
              <Alert variant="info">
                Each threshold is a percentage (0–100), managed per-org.{" "}
                {editable ? "You can edit these values." : "These are view-only for your role."}
              </Alert>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="thresholds-fields">
                {fields.map((field) => (
                  <ThresholdEditorRow key={field.key} field={field} editable={editable} />
                ))}
              </div>

              <Badge variant={editable ? "info" : "default"}>
                {editable ? "Editing thresholds (super-admin / admin)" : "View-only — ask an admin to change a threshold"}
              </Badge>
            </>
          )}
        </div>
      </FormSurface>
    </section>
  );
}
